import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["filterBar", "searchInput", "colorInput"]
  static values = { lastQuery: String, isOpen: Boolean }

  connect() {
    this.captureCurrentQuery()
    this.setupEventListeners()
  }

  disconnect() {
    this.cleanupEventListeners()
    this.teardownFocusTrap()
  }

  setupEventListeners() {
    this.boundHandleTurboLoad = this.handleTurboLoad.bind(this)
    this.boundHandleBeforeCache = this.handleBeforeCache.bind(this)
    this.boundHandlePanelKeydown = this.handlePanelKeydown.bind(this)
    document.addEventListener('turbo:load', this.boundHandleTurboLoad)
    document.addEventListener('turbo:before-cache', this.boundHandleBeforeCache)
  }

  cleanupEventListeners() {
    document.removeEventListener('turbo:load', this.boundHandleTurboLoad)
    document.removeEventListener('turbo:before-cache', this.boundHandleBeforeCache)
  }

  handleTurboLoad() {
    this.captureCurrentQuery()
  }

  // Ensure filter bar is closed before page is cached by Turbo
  // This prevents stale open state from being restored on navigation
  handleBeforeCache() {
    if (!this.hasFilterBarTarget) return

    this.closePanel()
  }

  removeFilter(event) {
    event.preventDefault()
    event.stopPropagation()

    // Close filter bar if open to prevent flicker during navigation
    this.closePanel()

    const { key, value } = this.getFilterDataFromEvent(event)
    const urlKey = this.normalizeUrlKey(key)

    this.captureCurrentQuery()
    const url = this.buildUpdatedUrl(urlKey, value)
    this.navigateToUrl(url)
  }

  getFilterDataFromEvent(event) {
    return {
      key: event.currentTarget.dataset.filterKey,
      value: event.currentTarget.dataset.filterValue
    }
  }

  normalizeUrlKey(key) {
    return key === "company_ids" ? "c" : key
  }

  buildUpdatedUrl(urlKey, valueToRemove) {
    // Start from current URL - this preserves starts_with and other params
    // (unlike buildUrlFromFormData which starts from form data)
    const url = new URL(window.location)
    const searchParams = new URLSearchParams(url.search)

    this.updateQueryParam(searchParams)
    this.updateFilterParams(searchParams, urlKey, valueToRemove)

    url.search = searchParams.toString()
    return url
  }

  updateQueryParam(searchParams) {
    if (this.lastQueryValue?.trim()) {
      searchParams.set('query', this.lastQueryValue)
    } else {
      searchParams.delete('query')
    }
  }

  updateFilterParams(searchParams, urlKey, valueToRemove) {
    // Label filter uses a distinct URL structure: label_filter[values][]
    if (urlKey === "label_filter") {
      const remaining = searchParams.getAll("label_filter[values][]").filter(v => v !== valueToRemove)
      searchParams.delete("label_filter[values][]")
      remaining.forEach(v => searchParams.append("label_filter[values][]", v))
      return remaining
    }

    // Handle array format (checkboxes): filters[key][]
    const arrayFilters = searchParams.getAll(`filters[${urlKey}][]`)
    const newArrayFilters = arrayFilters.filter(v => v !== valueToRemove)

    searchParams.delete(`filters[${urlKey}][]`)
    newArrayFilters.forEach(v => searchParams.append(`filters[${urlKey}][]`, v))

    // Handle non-array format (radio buttons): filters[key]
    const singleFilter = searchParams.get(`filters[${urlKey}]`)
    if (singleFilter === valueToRemove) {
      searchParams.delete(`filters[${urlKey}]`)
    }

    return newArrayFilters
  }

  navigateToUrl(url) {
    Turbo.visit(url.toString(), { action: "advance" })
  }

  submitFilter(event) {
    event.preventDefault()

    const formData = new FormData(event.target)
    this.captureCurrentQuery()
    this.appendQueryToFormData(formData)

    const url = this.buildUrlFromFormData(formData)
    this.navigateToUrl(url)
  }

  appendQueryToFormData(formData) {
    if (this.lastQueryValue?.trim()) {
      formData.append('query', this.lastQueryValue)
    }
  }

  buildUrlFromFormData(formData) {
    const searchParams = new URLSearchParams(formData)
    const url = new URL(window.location)

    // Preserve starts_with from current URL (not from stale form hidden fields)
    const currentParams = new URLSearchParams(url.search)
    const startsWithValue = currentParams.get('starts_with')
    if (startsWithValue) {
      searchParams.set('starts_with', startsWithValue)
    }

    url.search = searchParams.toString()
    return url
  }

  open(event) {
    event.preventDefault()
    this.showFilterBar()
    this.captureCurrentQuery()
    this.isOpenValue = true
    this.setupFocusTrap(event.currentTarget)
  }

  close(event) {
    event.preventDefault()
    this.closePanel()
  }

  closePanel() {
    if (!this.hasFilterBarTarget) return

    this.hideFilterBar()
    this.isOpenValue = false
    this.teardownFocusTrap()
  }

  showFilterBar() {
    if (!this.hasFilterBarTarget) return

    this.filterBarTarget.classList.add("table-filter-bar--active")
  }

  hideFilterBar() {
    if (!this.hasFilterBarTarget) return

    this.filterBarTarget.classList.remove("table-filter-bar--active")
  }

  setupFocusTrap(trigger) {
    if (!this.hasFilterBarTarget) return
    if (this.previouslyFocusedElement) return

    // Safari does not always focus buttons on click, so document.activeElement
    // falls back to the body. Use the trigger element in that case.
    const active = document.activeElement
    this.previouslyFocusedElement = (active && active !== document.body) ? active : trigger

    // Make the panel programmatically focusable so the Tab trap has a fallback
    // when no descendants are focusable yet.
    if (!this.filterBarTarget.hasAttribute('tabindex')) {
      this.filterBarTarget.setAttribute('tabindex', '-1')
    }

    document.addEventListener('keydown', this.boundHandlePanelKeydown)

    this.scheduleInitialFocus()
  }

  // Move focus into the panel, but only once it has finished sliding in. The
  // panel animates from off-screen over 0.3s, so focusing on the next frame
  // (while it is still translated out of view) makes the browser scroll the
  // page sideways toward the off-canvas field, introducing a horizontal
  // scrollbar (the first field is a TomSelect, which also opens its dropdown
  // on focus). Waiting for the transition to end focuses it on-screen instead.
  scheduleInitialFocus() {
    this.boundFocusInitialElement = this.focusInitialElement.bind(this)
    this.filterBarTarget.addEventListener('transitionend', this.boundFocusInitialElement)
    // Fallback for when no transition runs (reduced motion, an interrupted
    // transition, or the panel is already in place).
    this.initialFocusTimer = setTimeout(this.boundFocusInitialElement, 350)
  }

  focusInitialElement(event) {
    // transitionend bubbles and fires per-property; only react to the panel's
    // own slide (its transform), not a child's or some other property.
    if (event && event.type === 'transitionend' &&
        (event.target !== this.filterBarTarget || event.propertyName !== 'transform')) {
      return
    }

    this.cancelScheduledFocus()

    if (!this.previouslyFocusedElement) return
    if (this.filterBarTarget.contains(document.activeElement)) return

    if (!this.focusFirstFocusable()) {
      this.filterBarTarget.focus({ preventScroll: true })
    }
  }

  cancelScheduledFocus() {
    if (this.boundFocusInitialElement) {
      if (this.hasFilterBarTarget) {
        this.filterBarTarget.removeEventListener('transitionend', this.boundFocusInitialElement)
      }
      this.boundFocusInitialElement = null
    }
    if (this.initialFocusTimer) {
      clearTimeout(this.initialFocusTimer)
      this.initialFocusTimer = null
    }
  }

  teardownFocusTrap() {
    document.removeEventListener('keydown', this.boundHandlePanelKeydown)
    this.cancelScheduledFocus()

    if (this.previouslyFocusedElement && this.previouslyFocusedElement.isConnected) {
      this.previouslyFocusedElement.focus()
    }
    this.previouslyFocusedElement = null
  }

  handlePanelKeydown(event) {
    if (!this.hasFilterBarTarget) return

    if (event.key === "Escape") {
      event.preventDefault()
      this.closePanel()
      return
    }

    if (event.key === "Tab" && this.filterBarTarget.contains(document.activeElement)) {
      const focusable = this.getFocusableElements()
      if (focusable.length === 0) return

      const first = focusable[0]
      const last = focusable[focusable.length - 1]

      if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      } else if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      }
    }
  }

  getFocusableElements() {
    if (!this.hasFilterBarTarget) return []

    const selector = 'a[href], button, input, textarea, select, details, [tabindex]:not([tabindex="-1"])'
    // getClientRects() avoids offsetParent's false negatives for position:fixed elements.
    // tabIndex !== -1 catches tabindex="-1" on natively-focusable tags, which the selector misses.
    return Array.from(this.filterBarTarget.querySelectorAll(selector)).filter(
      (el) => !el.hasAttribute('disabled') && el.tabIndex !== -1 && el.getClientRects().length > 0
    )
  }

  focusFirstFocusable() {
    const focusable = this.getFocusableElements()
    if (focusable.length === 0) return false

    focusable[0].focus({ preventScroll: true })
    return true
  }

  // Color selection for filter sidebar
  selectColor(event) {
    event.preventDefault()

    const link = event.target.closest('.color-option')
    if (!link) return

    const colorValue = link.dataset.colorValue
    const circle = link.querySelector('.circle')
    const isCurrentlySelected = circle.classList.contains('selected')

    // Clear all selected states
    this.element.querySelectorAll('.circle').forEach(c => c.classList.remove('selected'))

    // If it wasn't selected, select it. If it was selected, leave it deselected
    if (!isCurrentlySelected) {
      circle.classList.add('selected')
      // Update hidden input with selected color
      if (this.hasColorInputTarget) {
        this.colorInputTarget.value = colorValue
      }
    } else {
      // Deselected - clear the hidden input
      if (this.hasColorInputTarget) {
        this.colorInputTarget.value = ''
      }
    }
  }

  captureCurrentQuery() {
    const searchInput = this.findSearchInput()
    if (searchInput && searchInput.value?.trim()) {
      this.lastQueryValue = searchInput.value
    } else {
      // Fallback: get query from current URL if search input is empty or not found
      const urlParams = new URLSearchParams(window.location.search)
      const urlQuery = urlParams.get('query')
      if (urlQuery) {
        this.lastQueryValue = urlQuery
      } else {
        // Clear lastQueryValue if both input and URL are empty
        this.lastQueryValue = ''
      }
    }
  }

  findSearchInput() {
    if (this.hasSearchInputTarget) {
      return this.searchInputTarget
    }
    return this.findSearchInputInDOM()
  }

  findSearchInputInDOM() {
    let currentElement = this.element

    while (currentElement) {
      const searchInput = this.searchInSiblings(currentElement)
      if (searchInput) return searchInput

      currentElement = currentElement.parentElement
    }

    return null
  }

  searchInSiblings(element) {
    const siblings = element.parentElement?.children || []

    for (const sibling of siblings) {
      if (sibling !== element) {
        const input = sibling.querySelector('[data-table-search-target="search"]')
        if (input) return input
      }
    }

    return null
  }
}
